import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useUser, useClerk, useSession } from '@clerk/vue';
import { api } from '../lib/api';
import { connectSocket, disconnectSocket } from '../lib/socket';
import type { User } from '@healthfare/shared';

const CLERK_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

export const useAuthStore = defineStore('auth', () => {
  // --- JWT state (used in both modes; in Clerk mode these stay null) ---
  const user = ref<User | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);

  // --- Clerk composables (only called when the plugin is installed) ---
  // The import above is safe; it's the *call* that throws when the plugin is absent.
  let clerkUser: ReturnType<typeof useUser>['user'];
  let isSignedIn: ReturnType<typeof useUser>['isSignedIn'];
  let session: ReturnType<typeof useSession>['session'];
  let clerk: ReturnType<typeof useClerk>;
  if (CLERK_ENABLED) {
    const u = useUser();
    clerkUser = u.user;
    isSignedIn = u.isSignedIn;
    session = useSession().session;
    clerk = useClerk();
  } else {
    clerkUser = ref(null) as any;
    isSignedIn = ref(false) as any;
    session = ref(null) as any;
    clerk = ref(null) as any;
  }

  // isAuthenticated: in Clerk mode, derive from Clerk session; otherwise from JWT token
  const isAuthenticated = computed(() => {
    if (CLERK_ENABLED) return !!isSignedIn.value;
    return !!accessToken.value;
  });

  /**
   * Returns a Bearer token for use in API requests.
   * In Clerk mode, fetches a short-lived session token; in JWT mode returns stored token.
   */
  async function getToken(): Promise<string | null> {
    if (CLERK_ENABLED) {
      if (!session.value) return null;
      return session.value.getToken();
    }
    return accessToken.value;
  }

  async function init() {
    if (CLERK_ENABLED) {
      // In Clerk mode, authentication state is managed by the Clerk plugin.
      // Watch for Clerk session and connect socket once a token is available.
      if (isSignedIn.value && session.value) {
        const token = await session.value.getToken();
        if (token) connectSocket(token);
      }
      return;
    }

    // --- JWT mode ---
    const storedToken = localStorage.getItem('accessToken');
    const storedRefresh = localStorage.getItem('refreshToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedRefresh && storedUser) {
      accessToken.value = storedToken;
      refreshToken.value = storedRefresh;
      user.value = JSON.parse(storedUser);

      // Try to verify token, refresh if expired
      try {
        await api.get('/auth/me');
        connectSocket(storedToken);
      } catch {
        try {
          await refresh();
        } catch {
          logout();
        }
      }

      // Auto-refresh token every 12 minutes (before 15min expiry)
      setInterval(() => {
        refresh().catch(() => {});
      }, 12 * 60 * 1000);
    }
  }

  async function login(email: string, password: string) {
    // login() is only called in JWT mode — Clerk handles sign-in via its own UI
    const response = await api.post('/auth/login', { email, password });
    const data = response.data.data;

    user.value = data.user;
    accessToken.value = data.tokens.accessToken;
    refreshToken.value = data.tokens.refreshToken;

    localStorage.setItem('accessToken', data.tokens.accessToken);
    localStorage.setItem('refreshToken', data.tokens.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));

    connectSocket(data.tokens.accessToken);
  }

  async function refresh() {
    if (!refreshToken.value) throw new Error('No refresh token');

    const response = await api.post('/auth/refresh', {
      refreshToken: refreshToken.value,
    });
    const data = response.data.data;

    accessToken.value = data.accessToken;
    refreshToken.value = data.refreshToken;

    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);

    connectSocket(data.accessToken);
  }

  function logout() {
    if (CLERK_ENABLED) {
      disconnectSocket();
      (clerk.value as any)?.signOut?.();
      return;
    }

    if (refreshToken.value) {
      api.post('/auth/logout', { refreshToken: refreshToken.value }).catch(() => {});
    }

    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    disconnectSocket();
  }

  // In Clerk mode, map Clerk's UserResource to our local User shape so existing
  // components (which read .full_name / .email / .role) keep working unchanged.
  const mappedClerkUser = computed<User | null>(() => {
    const cu = clerkUser.value as any;
    if (!cu) return null;
    return {
      id: cu.id,
      full_name: cu.fullName || cu.firstName || cu.username || '',
      email: cu.primaryEmailAddress?.emailAddress || cu.emailAddresses?.[0]?.emailAddress || '',
      avatar_url: cu.imageUrl ?? null,
      role: (cu.publicMetadata?.role as 'owner' | 'admin' | 'agent') || 'agent',
      status: 'active',
      last_seen_at: null,
      created_at: cu.createdAt?.toISOString?.() ?? new Date().toISOString(),
      updated_at: cu.updatedAt?.toISOString?.() ?? new Date().toISOString(),
    } as User;
  });

  return {
    user: CLERK_ENABLED ? mappedClerkUser : user,
    accessToken,
    refreshToken,
    isAuthenticated,
    getToken,
    init,
    login,
    refresh,
    logout,
  };
});
